import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import Header from "@/pages/home/components/Header";
import Footer from "@/pages/home/components/Footer";

interface Post {
  id: number;
  title: string;
  content: string;
  category: string;
  author_id: string;
  views: number;
  likes: number;
  created_at: string;
}

interface Comment {
  id: number;
  post_id: number;
  author_id: string;
  content: string;
  created_at: string;
  profiles: { nickname: string } | null;
}

interface Profile {
  nickname: string;
}

const categoryLabels: Record<string, string> = {
  자유게시판: "자유게시판",
  이용후기: "이용후기",
  질문답변: "질문답변",
  정보공유: "정보공유",
  먹튀제보: "먹튀제보",
  스포츠분석: "스포츠분석",
};

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}`;
}

export default function PostDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [post, setPost] = useState<Post | null>(null);
  const [authorProfile, setAuthorProfile] = useState<Profile | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [commentText, setCommentText] = useState("");
  const [loading, setLoading] = useState(true);
  const [commentLoading, setCommentLoading] = useState(false);
  const [error, setError] = useState("");
  const [deletingCommentId, setDeletingCommentId] = useState<number | null>(null);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [likeLoading, setLikeLoading] = useState(false);

  const postId = Number(id);

  const fetchPost = useCallback(async () => {
    if (!postId || isNaN(postId)) {
      setError("존재하지 않는 게시글입니다");
      setLoading(false);
      return;
    }

    try {
      const { data, error: fetchError } = await supabase
        .from("posts")
        .select("*")
        .eq("id", postId)
        .single();

      if (fetchError || !data) {
        setError("게시글을 찾을 수 없습니다");
        setLoading(false);
        return;
      }

      setPost(data);

      // Increment view count (fire and forget)
      supabase
        .from("posts")
        .update({ views: (data.views || 0) + 1 })
        .eq("id", postId)
        .then(() => {});

      // Fetch author profile
      if (data.author_id) {
        const { data: profileData } = await supabase
          .from("profiles")
          .select("nickname")
          .eq("id", data.author_id)
          .single();

        if (profileData) {
          setAuthorProfile(profileData);
        }
      }
    } catch {
      setError("게시글을 불러오는 중 오류가 발생했습니다");
    } finally {
      setLoading(false);
    }
  }, [postId]);

  const fetchComments = useCallback(async () => {
    try {
      const { data, error: commentsError } = await supabase
        .from("comments")
        .select("*, profiles(nickname)")
        .eq("post_id", postId)
        .order("created_at", { ascending: true });

      if (!commentsError && data) {
        setComments(data as Comment[]);
      }
    } catch {
      // Silently fail for comments
    }
  }, [postId]);

  useEffect(() => {
    fetchPost();
    fetchComments();
  }, [fetchPost, fetchComments]);

  const fetchLikeStatus = useCallback(async () => {
    if (!user || !postId) return;
    try {
      const { data, error: likeError } = await supabase
        .from("post_likes")
        .select("id")
        .eq("post_id", postId)
        .eq("user_id", user.id)
        .maybeSingle();

      if (!likeError) {
        setLiked(!!data);
      }
    } catch {
      // Silently fail
    }
  }, [user, postId]);

  const fetchLikeCount = useCallback(async () => {
    if (!postId) return;
    try {
      const { count, error: countError } = await supabase
        .from("post_likes")
        .select("*", { count: "exact", head: true })
        .eq("post_id", postId);

      if (!countError && count !== null) {
        setLikeCount(count);
      }
    } catch {
      // Silently fail
    }
  }, [postId]);

  useEffect(() => {
    fetchLikeStatus();
    fetchLikeCount();
  }, [fetchLikeStatus, fetchLikeCount]);

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) return;
    if (!commentText.trim()) return;
    if (commentText.length > 500) return;

    setCommentLoading(true);

    try {
      const { error: insertError } = await supabase.from("comments").insert({
        post_id: postId,
        author_id: user.id,
        content: commentText.trim(),
      });

      if (insertError) return;

      setCommentText("");
      await fetchComments();
    } catch {
      // Silently fail
    } finally {
      setCommentLoading(false);
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    setDeletingCommentId(commentId);

    try {
      const { error: deleteError } = await supabase
        .from("comments")
        .delete()
        .eq("id", commentId);

      if (deleteError) return;

      await fetchComments();
    } catch {
      // Silently fail
    } finally {
      setDeletingCommentId(null);
    }
  };

  const handleDeletePost = async () => {
    if (!window.confirm("정말로 삭제하시겠습니까?")) return;

    try {
      const { error: deleteError } = await supabase
        .from("posts")
        .delete()
        .eq("id", postId);

      if (deleteError) return;

      navigate("/", { replace: true });
    } catch {
      // Silently fail
    }
  };

  const handleToggleLike = async () => {
    if (!user) return;
    if (likeLoading) return;

    setLikeLoading(true);

    try {
      if (liked) {
        // Unlike
        const { error: deleteError } = await supabase
          .from("post_likes")
          .delete()
          .eq("post_id", postId)
          .eq("user_id", user.id);

        if (deleteError) return;

        setLiked(false);
        const newCount = Math.max(0, likeCount - 1);
        setLikeCount(newCount);

        // Sync posts.likes
        await supabase
          .from("posts")
          .update({ likes: newCount })
          .eq("id", postId);
      } else {
        // Like
        const { error: insertError } = await supabase
          .from("post_likes")
          .insert({
            post_id: postId,
            user_id: user.id,
          });

        if (insertError) return;

        setLiked(true);
        const newCount = likeCount + 1;
        setLikeCount(newCount);

        // Sync posts.likes
        await supabase
          .from("posts")
          .update({ likes: newCount })
          .eq("id", postId);
      }
    } catch {
      // Silently fail
    } finally {
      setLikeLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background-50">
        <Header />
        <div className="max-w-[800px] mx-auto px-3 md:px-4 py-10 flex items-center justify-center">
          <div className="flex items-center gap-2 text-foreground-600">
            <span className="w-4 h-4 flex items-center justify-center animate-spin">
              <i className="ri-loader-4-line" />
            </span>
            <span className="text-sm">불러오는 중...</span>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-background-50">
        <Header />
        <div className="max-w-[800px] mx-auto px-3 md:px-4 py-10">
          <div className="text-center py-10">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
              <span className="w-8 h-8 flex items-center justify-center text-foreground-600">
                <i className="ri-error-warning-line text-2xl" />
              </span>
            </div>
            <h2 className="text-lg font-bold text-foreground-500 mb-2">{error || "게시글이 존재하지 않습니다"}</h2>
            <p className="text-sm text-foreground-600 mb-4">삭제되었거나 잘못된 경로입니다</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-foreground-50 text-sm font-bold rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              홈으로 돌아가기
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const isAuthor = user?.id === post.author_id;

  return (
    <div className="min-h-screen bg-background-50">
      <Header />

      <div className="max-w-[800px] mx-auto px-3 md:px-4 py-6">
        {/* Back button */}
        <div className="mb-6">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-foreground-600 hover:text-foreground-600 transition-colors"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line" />
            </span>
            목록으로
          </Link>
        </div>

        {/* Post Content */}
        <article className="bg-background-100 rounded-xl border border-background-300 overflow-hidden">
          {/* Post Header */}
          <div className="px-5 md:px-6 py-5 border-b border-background-300">
            <div className="flex items-center gap-2 mb-3">
              <span className="px-2 py-0.5 text-[10px] font-bold bg-primary-500/15 text-primary-400 rounded whitespace-nowrap">
                {categoryLabels[post.category] || post.category}
              </span>
            </div>
            <h1 className="text-xl md:text-2xl font-bold text-foreground-800 mb-3 leading-snug">
              {post.title}
            </h1>
            <div className="flex items-center gap-3 text-xs text-foreground-600 flex-wrap">
              <div className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-user-line" />
                </span>
                <span className="text-foreground-600 font-medium">
                  {authorProfile?.nickname || "알 수 없음"}
                </span>
              </div>
              <span className="text-foreground-500">|</span>
              <div className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-time-line" />
                </span>
                <span>{formatDate(post.created_at)}</span>
              </div>
              <span className="text-foreground-500">|</span>
              <div className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-eye-line" />
                </span>
                <span>조회 {post.views + 1}</span>
              </div>
            </div>
          </div>

          {/* Post Body */}
          <div className="px-5 md:px-6 py-6">
            <div className="text-sm text-foreground-600 leading-relaxed whitespace-pre-wrap break-words">
              {post.content}
            </div>
          </div>

          {/* Post Actions */}
          <div className="px-5 md:px-6 py-4 border-t border-background-300 flex items-center justify-between">
            <div className="flex items-center gap-1">
              <button
                onClick={handleToggleLike}
                disabled={likeLoading}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                  liked
                    ? "text-primary-400 bg-primary-500/10"
                    : "text-foreground-600 hover:text-primary-400 hover:bg-primary-500/10"
                }`}
              >
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className={liked ? "ri-thumb-up-fill" : "ri-thumb-up-line"} />
                </span>
                <span>좋아요 {likeCount}</span>
              </button>
              <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-foreground-600 hover:text-foreground-600 hover:bg-background-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-chat-3-line" />
                </span>
                <span>댓글 {comments.length}</span>
              </button>
            </div>

            {isAuthor && (
              <button
                onClick={handleDeletePost}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-delete-bin-line" />
                </span>
                삭제
              </button>
            )}
          </div>
        </article>

        {/* Comments Section */}
        <section className="mt-6 bg-background-100 rounded-xl border border-background-300 overflow-hidden">
          <div className="px-5 md:px-6 py-4 border-b border-background-300">
            <h2 className="text-sm font-bold text-foreground-500 flex items-center gap-2">
              <span className="w-4 h-4 flex items-center justify-center text-primary-400">
                <i className="ri-chat-3-line" />
              </span>
              댓글 <span className="text-primary-400">{comments.length}</span>
            </h2>
          </div>

          {/* Comment List */}
          <div className="divide-y divide-background-300">
            {comments.length === 0 ? (
              <div className="px-5 md:px-6 py-10 text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-background-200 flex items-center justify-center">
                  <span className="w-6 h-6 flex items-center justify-center text-foreground-600">
                    <i className="ri-chat-off-line" />
                  </span>
                </div>
                <p className="text-sm text-foreground-600">아직 댓글이 없습니다</p>
                <p className="text-xs text-foreground-600 mt-1">첫 댓글을 작성해보세요</p>
              </div>
            ) : (
              comments.map((comment) => (
                <div key={comment.id} className="px-5 md:px-6 py-4 group">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0">
                      <span className="w-8 h-8 rounded-full bg-background-200 border border-background-300 flex items-center justify-center flex-shrink-0">
                        <span className="w-4 h-4 flex items-center justify-center text-foreground-600">
                          <i className="ri-user-line text-sm" />
                        </span>
                      </span>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-bold text-foreground-600">
                            {comment.profiles?.nickname || "알 수 없음"}
                          </span>
                          <span className="text-[10px] text-foreground-600">
                            {formatDate(comment.created_at)}
                          </span>
                        </div>
                        <p className="text-sm text-foreground-600 leading-relaxed break-words">
                          {comment.content}
                        </p>
                      </div>
                    </div>

                    {user?.id === comment.author_id && (
                      <button
                        onClick={() => handleDeleteComment(comment.id)}
                        disabled={deletingCommentId === comment.id}
                        className="flex-shrink-0 opacity-0 group-hover:opacity-100 w-7 h-7 flex items-center justify-center rounded text-foreground-600 hover:text-red-400 hover:bg-red-500/10 transition-all cursor-pointer"
                      >
                        <span className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-close-line" />
                        </span>
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Comment Input */}
          <div className="px-5 md:px-6 py-4 border-t border-background-300">
            {user ? (
              <form onSubmit={handleAddComment} className="flex items-start gap-3">
                <span className="w-8 h-8 rounded-full bg-background-200 border border-background-300 flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="w-4 h-4 flex items-center justify-center text-primary-400">
                    <i className="ri-user-fill text-sm" />
                  </span>
                </span>
                <div className="flex-1 min-w-0">
                  <textarea
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder="댓글을 입력하세요"
                    rows={2}
                    maxLength={500}
                    className="w-full bg-background-200 rounded-lg px-4 py-2.5 text-sm text-foreground-500 placeholder-foreground-500 border border-background-300 focus:border-primary-500/50 outline-none transition-colors resize-none"
                  />
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[10px] text-foreground-600">{commentText.length}/500</span>
                    <button
                      type="submit"
                      disabled={!commentText.trim() || commentLoading}
                      className="px-4 py-1.5 bg-primary-500 text-foreground-50 text-xs font-bold rounded-md hover:bg-primary-600 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
                    >
                      {commentLoading ? "등록 중..." : "댓글 작성"}
                    </button>
                  </div>
                </div>

                {/* honeypot */}
                <div className="hp-field">
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                  />
                </div>
              </form>
            ) : (
              <div className="text-center py-3">
                <p className="text-sm text-foreground-600">
                  댓글을 작성하려면{" "}
                  <Link to="/login" className="text-primary-400 hover:text-primary-300 font-bold">
                    로그인
                  </Link>
                  이 필요합니다
                </p>
              </div>
            )}
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}